import React, { useState, useEffect, useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import cx from 'clsx'
// Project instance — the stock one cannot dedupe our named z-index
// utilities, so a containerStyle of z-confirm/z-nested was landing
// alongside the default z-modal instead of replacing it.
import { twMerge } from '@helpers/twMerge'
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'

import Button from '@components/Button'

import { isTopmostOverlay, registerOverlay } from '@helpers/overlayStack'

import styles from './index.module.css'

import { NavigateDirection } from '@type/common'

interface PopupProps {
  title?: string
  id?: string
  onClose?: () => void
  className?: string | null
  headerClassName?: string | null
  containerStyle?: string | null
  closeButtonStyle?: string | null
  allowCloseOnEsc?: boolean
  allowCloseOnMissClick?: boolean

  onNavigate?(direction: NavigateDirection): void

  canNavigate?(direction: NavigateDirection): boolean
}

const Popup: React.FC<React.PropsWithChildren<PopupProps>> = ({
  children,
  title,
  className,
  headerClassName,
  id,
  containerStyle,
  closeButtonStyle,
  allowCloseOnEsc = true,
  allowCloseOnMissClick = true,
  onClose,
  onNavigate,
  canNavigate = () => false,
}) => {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  /** True if pointerdown for this interaction started inside the white dialog panel (not overlay/chevrons). */
  const pointerDownStartedInsidePanelRef = useRef(false)

  useEffect(() => {
    const el = document.getElementById('popup-container')

    if (el) {
      setContainer(el)
    }
  }, [])

  const handleClose: React.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      e.stopPropagation()
      if (onClose) {
        onClose()
      }
    },
    [onClose],
  )

  const stopPropagation: React.MouseEventHandler<HTMLElement> = useCallback((e) => {
    e.stopPropagation()
  }, [])

  const handleOverlayPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!panelRef.current) return
    pointerDownStartedInsidePanelRef.current = panelRef.current.contains(e.target as Node)
  }, [])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!allowCloseOnMissClick) return
      if (e.target !== e.currentTarget) return
      if (!pointerDownStartedInsidePanelRef.current) {
        handleClose(e)
      }
      pointerDownStartedInsidePanelRef.current = false
    },
    [allowCloseOnMissClick, handleClose],
  )

  const escToken = useRef({})

  // Registration is keyed on actually being rendered: this component returns
  // null until the portal container exists, and an unrendered popup must not
  // hold the topmost Escape slot. It deliberately does not depend on
  // `allowCloseOnEsc` or on `onClose`'s identity — re-registering would push
  // this popup back above a nested one that was opened after it.
  useEffect(() => {
    if (!container) return undefined
    return registerOverlay(escToken.current)
  }, [container])

  useEffect(() => {
    // Defined inside the effect and keyed on allowCloseOnEsc so that a caller
    // toggling the flag on a *reused* Popup instance (e.g. a create step with
    // Esc enabled that transitions into a reveal step with Esc disabled)
    // actually re-binds. With allowCloseOnEsc left out of the deps the listener
    // added on the first render would leak into every later state.
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation()
      // The listener is registered for as long as the popup is mounted and
      // allowCloseOnEsc only gates onClose: a popup that refuses Escape should
      // absorb it, not hand it to the layer beneath.
      if (e.key === 'Escape' && allowCloseOnEsc && isTopmostOverlay(escToken.current)) {
        onClose?.()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    // Hiding the body scrollbar widens the viewport and shifts the whole page;
    // compensate with equivalent padding. Save the inline values rather than
    // hardcoding the restore, so stacked popups unwind to the correct state.
    const { overflow: prevOverflow, paddingRight: prevPaddingRight } = document.body.style
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
      document.body.style.paddingRight = prevPaddingRight
    }
  }, [onClose, allowCloseOnEsc])

  const disableNavigateRight = !canNavigate(NavigateDirection.right)
  const disableNavigateLeft = !canNavigate(NavigateDirection.left)

  return container
    ? ReactDOM.createPortal(
        <div
          className={twMerge(
            cx(
              'fixed z-modal flex bg-gray-900 bg-opacity-60 w-full h-full text-black justify-center items-center overflow-auto',
              containerStyle,
            ),
          )}
          onPointerDown={handleOverlayPointerDown}
          onClick={allowCloseOnMissClick ? handleOverlayClick : undefined}
          id={id ? `close-missclick-${id}` : 'close-missclick-popup'}
        >
          {onNavigate && (
            <Button
              color="white"
              onClick={(e) => {
                stopPropagation(e)
                if (!disableNavigateLeft) {
                  onNavigate(NavigateDirection.left)
                }
              }}
              className="w-14 h-14 rounded-[20px] border-none disabled:opacity-50 !pointer-events-auto hidden md:block"
              disabled={disableNavigateLeft}
            >
              <IconChevronLeft className="w-6 h-6" />
            </Button>
          )}
          <div
            ref={panelRef}
            className={twMerge(
              cx(
                'max-h-[90%] rounded-base bg-white p-4 opacity-100 max-w-md w-full my-auto overflow-auto relative',
                styles.popup,
                className,
              ),
            )}
            onClick={stopPropagation}
          >
            {title && (
              <h4 className={twMerge('text-lg leading-6 font-medium border-b mb-3 pb-2', headerClassName)}>{title}</h4>
            )}
            {onClose && (
              <button
                type="button"
                id={id ? `close-${id}` : 'close-popup'}
                onClick={handleClose}
                className={cx(
                  'absolute right-2.5 top-2.5 z-50 p-0.5 min-h-0 h-auto border rounded-base text-brand_primary hover:bg-slate-50 transition-colors',
                  closeButtonStyle,
                )}
              >
                <IconX className="h-5 w-5" />
              </button>
            )}
            {children}
          </div>
          {onNavigate && (
            <Button
              color="white"
              onClick={(e) => {
                stopPropagation(e)
                if (!disableNavigateRight) {
                  onNavigate(NavigateDirection.right)
                }
              }}
              disabled={disableNavigateRight}
              className="w-14 h-14 rounded-[20px] border-none disabled:opacity-70 !pointer-events-auto hidden md:block"
            >
              <IconChevronRight className="w-6 h-6" />
            </Button>
          )}
        </div>,
        container,
      )
    : null
}

export default Popup
export { usePopupHandlers } from './usePopupHandler'
